// Shared detection of Outlook's quoted reply/forward boundary in a compose body.
//
// Used by two flows:
//   - composeBridge.applyRewriteToBody — splice the rewrite ABOVE the quote.
//   - mailboxContext.composeModeContext — strip the quote so the user's authored
//     message (draftBody) doesn't include the prior thread.
//
// We rely on the DOM markup, not plain-text heuristics: Outlook's text export
// often glues the user's line to the quoted header ("hiFrom: …") with no line
// break, which defeats line-anchored regexes. Parsing the HTML and matching the
// stable quote markers is reliable across OWA and classic desktop.

/** Find the direct child of <body> that begins Outlook's quoted reply/forward
 * block, or null when there's no quote (a brand-new email). We match a semantic
 * marker first (stable across clients) and only fall back to <hr>/<blockquote>,
 * so a signature rule isn't mistaken for the quote boundary. The marker is
 * usually nested, so we walk UP to the top-level child that contains it — cutting
 * there keeps the whole quote subtree (incl. the <hr>) intact and well-formed. */
export function findQuoteBoundaryChild(doc: Document, allowWeakFallback = true): Element | null {
  const body = doc.body;
  if (!body) return null;
  let marker = body.querySelector('#divRplyFwdMsg, #appendonsend, a[name="_MailOriginal" i]');
  // <hr>/<blockquote> are weak signals (a signature divider looks the same), so
  // only use them when the caller can tolerate a false positive — e.g. the
  // insert flow (worst case: it keeps a bit extra). Draft capture opts out, to
  // avoid truncating the user's own message at a signature rule.
  if (!marker && allowWeakFallback) marker = body.querySelector("hr, blockquote");
  if (!marker) return null;

  let node: Element = marker;
  while (node.parentElement && node.parentElement !== body) {
    node = node.parentElement;
  }
  return node;
}

/** Convert an HTML fragment to plain text, preserving paragraph/line breaks
 * (block tags → newlines), then normalise whitespace. */
function htmlToText(html: string): string {
  if (!html) return "";
  let s = html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ");
  try {
    s = new DOMParser().parseFromString(s, "text/html").body.textContent ?? "";
  } catch {
    s = s.replace(/<[^>]+>/g, "");
  }
  return s
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Given the compose body HTML, return the user's authored message as plain text
 * (the part ABOVE Outlook's quoted reply/forward block). Returns null when no
 * quote boundary is detected (e.g. a brand-new email or unrecognised markup), so
 * the caller can fall back to its own heuristic. */
export function authoredTextFromBodyHtml(bodyHtml: string): string | null {
  if (!bodyHtml) return null;
  try {
    const doc = new DOMParser().parseFromString(bodyHtml, "text/html");
    const body = doc.body;
    if (!body) return null;

    // Semantic markers only — don't let a signature <hr> truncate the user's text.
    const marker = body.querySelector(
      '#divRplyFwdMsg, #appendonsend, a[name="_MailOriginal" i]',
    );
    if (!marker) return null;

    // Everything BEFORE the marker in document order is the user's authored
    // message. We must NOT remove the marker's top-level <body> child (what
    // findQuoteBoundaryChild returns): classic desktop Outlook is Word-based and
    // wraps the authored text AND the quote inside a single container <div>
    // (e.g. WordSection1), so removing that wrapper drops the authored text too.
    // Instead, walk from the marker up to <body>, deleting every node that
    // FOLLOWS the marker at each level, then the marker itself — this strips the
    // quote while leaving the authored content inside any shared ancestor intact.
    // (Done with sibling removal rather than a Range, which has quirks in the
    // legacy webview classic Outlook can host.)
    const following: ChildNode[] = [];
    let cur: Node | null = marker;
    while (cur && cur !== body) {
      for (let sib = cur.nextSibling; sib; sib = sib.nextSibling) {
        following.push(sib);
      }
      cur = cur.parentNode;
    }
    following.forEach((node) => node.parentNode?.removeChild(node));
    marker.parentNode?.removeChild(marker);

    return htmlToText(body.innerHTML);
  } catch {
    return null;
  }
}
